import React from 'react';
import { analyticsSectionClass } from './analyticsUi.js';

const alignClass = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
};

/**
 * Responsive analytics table.
 * Mobile (< md): stacked cards with label/value rows
 * Tablet & desktop (md+): standard table with horizontal scroll when needed
 */
function AnalyticsTable({
  title,
  columns,
  rows,
  emptyMessage = 'No data available',
  className = '',
  tableMinWidth = '640px',
  footerRow = null,
}) {
  if (!rows || rows.length === 0) {
    return (
      <div className={`${analyticsSectionClass} ${className}`}>
        {title && <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-3 sm:mb-4">{title}</h3>}
        <p className="text-gray-400 text-sm text-center py-6">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className={`${analyticsSectionClass} ${className}`}>
      {title && <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-3 sm:mb-4">{title}</h3>}

      {/* Mobile card list */}
      <div className="md:hidden space-y-3">
        {rows.map((row, rowIndex) => (
          <div
            key={row.key ?? rowIndex}
            className="rounded-xl border border-gray-100 bg-gray-50/80 p-3 space-y-2"
          >
            {columns.map((col, colIndex) => {
              const cell = row.cells[colIndex];
              if (cell == null || cell === '') return null;
              return (
                <div
                  key={col.key ?? colIndex}
                  className={`flex items-start justify-between gap-3 text-sm ${
                    colIndex === 0 ? 'pb-1 border-b border-gray-200/80' : ''
                  }`}
                >
                  <span className="text-gray-500 text-xs font-medium shrink-0 max-w-[45%]">
                    {col.header}
                  </span>
                  <span
                    className={`font-medium text-gray-800 min-w-0 break-words ${
                      alignClass[col.align || 'left']
                    } ${colIndex === 0 ? 'text-sm' : ''}`}
                  >
                    {cell}
                  </span>
                </div>
              );
            })}
          </div>
        ))}
        {footerRow && (
          <div className="rounded-xl border border-blue-200 bg-blue-50 p-3 space-y-2 font-semibold">
            {columns.map((col, colIndex) => {
              const cell = footerRow.cells[colIndex];
              if (cell == null || cell === '') return null;
              return (
                <div key={col.key ?? colIndex} className="flex items-start justify-between gap-3 text-sm">
                  <span className="text-gray-600 text-xs font-semibold shrink-0">{col.header}</span>
                  <span className={`text-gray-900 ${alignClass[col.align || 'left']}`}>{cell}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Tablet & desktop table */}
      <div className="hidden md:block overflow-x-auto -mx-3 sm:-mx-4 lg:-mx-6 px-3 sm:px-4 lg:px-6">
        <table className="min-w-full divide-y divide-gray-200 text-sm" style={{ minWidth: tableMinWidth }}>
          <thead className="bg-gray-50">
            <tr>
              {columns.map((col, i) => (
                <th
                  key={col.key ?? i}
                  className={`px-3 lg:px-4 py-2.5 lg:py-3 text-xs font-medium text-gray-500 uppercase tracking-wide ${
                    alignClass[col.align || 'left']
                  }`}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {rows.map((row, rowIndex) => (
              <tr key={row.key ?? rowIndex} className={`hover:bg-gray-50 ${row.className || ''}`}>
                {row.cells.map((cell, cellIndex) => (
                  <td
                    key={cellIndex}
                    className={`px-3 lg:px-4 py-2.5 lg:py-3 text-sm text-gray-800 ${
                      alignClass[columns[cellIndex]?.align || 'left']
                    } ${columns[cellIndex]?.cellClassName || ''}`}
                  >
                    {cell ?? '-'}
                  </td>
                ))}
              </tr>
            ))}
            {footerRow && (
              <tr className={`font-semibold ${footerRow.className || 'bg-blue-50'}`}>
                {footerRow.cells.map((cell, cellIndex) => (
                  <td
                    key={cellIndex}
                    className={`px-3 lg:px-4 py-2.5 lg:py-3 text-sm ${
                      alignClass[columns[cellIndex]?.align || 'left']
                    }`}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default AnalyticsTable;
